import { useGeneratePath } from 'platform-components';
import { useLocation } from 'react-router-dom';
import {
    HeaderNavigationItem,
    HeaderNavigationSubmenu,
    HeaderMixedNavItem,
} from '@edu-frontend/modules/src/features/baseHeader';

import { FEATURES, TEACHER_COMMON_MENU_ITEMS, TEACHER_HELP_MENU_ITEMS } from '@yandex-int/k-common/typings';
import type { useNewLabPathAccess } from './useNewLabPathAccess';
import { getGetPattern } from './getGetPattern';
import { mapMatchPath } from './mapMatchPath';

type AdditionalOpts = {
    metricSlug?: string;
    notificationId?: string;
    target?: '_blank';
};

export type SubmenuLinkParams = Omit<HeaderNavigationSubmenu, 'hasNotification'> & AdditionalOpts;
export type LinkParams = Omit<HeaderNavigationItem, 'hasNotification'> & AdditionalOpts;

export function isSubmenuParams(item: unknown): item is SubmenuLinkParams {
    return (item as SubmenuLinkParams).items !== undefined;
}

const getCurrentSpecCourseUrl = ({
    classId,
    specCourseId,
    staticUrls,
}: {
    classId?: number | null;
    specCourseId?: number | null;
    staticUrls: Record<string, string>;
}): string => {
    if (!classId) {
        return staticUrls.labSpecCourse;
    }

    if (!specCourseId) {
        return staticUrls.labClassSpecCourseStub;
    }

    return staticUrls.labClassSpecCourse;
};

interface GetLinksParams {
    classId?: number;
    featureFlags: Partial<Record<FEATURES, boolean>> | null;
    i18n: (id: string) => string;
    isContentManager?: boolean;
    isInformaticsTeacher: boolean;
    location: ReturnType<typeof useLocation>;
    notificationIds: Record<string, number | undefined>;
    specCourseId: number | null;
    staticUrls: Record<string, string>;
    isMenuDisabled?: boolean;
    subject?: string;
    viewedNotifications: Set<number>;
    onLinkPress: (opts: { metricSlug?: string; notificationId?: number }) => void;
    getRouterPath: ReturnType<typeof useGeneratePath>;
    isAuthenticatedUser: boolean;
    newLab: ReturnType<typeof useNewLabPathAccess>;
}

// eslint-disable-next-line complexity
function mapLinkParamsToNavItems({
    item,
    notificationIds,
    onPress,
    viewedNotifications,
    classId,
    subject,
    specCourseId,
    getRouterPath,
    search,
}: {
    item: LinkParams | SubmenuLinkParams;
    notificationIds: Record<string, number | undefined>;
    onPress: ({}) => void;
    viewedNotifications: Set<number>;
    classId?: number;
    subject?: string;
    specCourseId: number | null;
    getRouterPath: ReturnType<typeof useGeneratePath>;
    search?: string;
}): HeaderMixedNavItem {
    const notificationId = item.notificationId ? notificationIds[item.notificationId] : undefined;

    if (!isSubmenuParams(item) && item.href) {
        item.href = `${getRouterPath(item.href, {
            classId,
            teamId: classId,
            subject,
            courseId: item.id === 'speccourse' && specCourseId ? specCourseId : undefined,
        })}${item.id === 'cms' ? '' : search}`;
    }

    if (isSubmenuParams(item)) {
        return {
            id: item.id,
            className: item.className,
            content: item.content,
            hasNotification: Boolean(notificationId && !viewedNotifications.has(notificationId)),
            onPress: item.onPress,
            items: item.items.map((subItem) => {
                return mapLinkParamsToNavItems({
                    item: subItem as LinkParams,
                    onPress,
                    viewedNotifications,
                    notificationIds,
                    classId,
                    subject,
                    specCourseId,
                    getRouterPath,
                    search,
                }) as HeaderNavigationItem;
            }),
            disabled: item.disabled,
            target: item.target,
        };
    }

    const originalOnPress = item.onPress;

    item.onPress = () => {
        onPress({
            notificationId: notificationId,
            metricSlug: item.metricSlug,
        });
        if (originalOnPress) {
            originalOnPress();
        }
    };

    return {
        id: item.id,
        className: item.className,
        content: item.content,
        href: (item as LinkParams).href,
        active: (item as LinkParams).active,
        hasNotification: Boolean(notificationId && !viewedNotifications.has(notificationId)),
        onPress: item.onPress,
        disabled: item.disabled,
        target: item.target,
    };
}

// eslint-disable-next-line complexity
export function getLinksParams({
    classId,
    featureFlags,
    i18n,
    isContentManager,
    isInformaticsTeacher,
    location,
    notificationIds,
    viewedNotifications,
    onLinkPress,
    specCourseId,
    staticUrls,
    subject,
    isMenuDisabled,
    getRouterPath,
    newLab,
}: GetLinksParams): Array<LinkParams | SubmenuLinkParams> {
    const {
        cmHome,
        labLessons,
        labJournal,
        labClassJournal,
        labClassLessons,
        labCourseJournal,
        labStudentSubject,
        labTeacherTrainingCourses,
        labClass,
        labClasses,
        labSpecCourse,
        labClassSpecCourseStub,
        labPortfolio,
        labLibrarySubjectCollection,
        labLibrarySubjectCommonCourseContainer,
        deepLabLibrary,
        labLibrarySubject,
        deepLabLibraryMain,
    } = staticUrls;

    const { pathname, search } = location;

    const config = {
        cmHome,
        labLessons,
        labJournal,
        labClassJournal,
        labClassLessons,
        labCourseJournal,
        labStudentSubject,
        labTeacherTrainingCourses,
        labClass,
        labClasses,
        labSpecCourse,
        labClassSpecCourseStub,
        labPortfolio,
        labLibrarySubjectCollection,
        labLibrarySubjectCommonCourseContainer,
        deepLabLibrary,
        labLibrarySubject,
        deepLabLibraryMain,
    };

    const isMatchPath = mapMatchPath(config, pathname);

    const getPattern = getGetPattern({ teamId: classId, subject });

    const { newLabAccess, newLabPath } = newLab;
    const oldLabPath = {
        materials: getPattern({
            noParams: staticUrls.deepLabLibrary,
            withTeamId: staticUrls.labLibrarySubjectInformatics,
        }),
        lessons: getPattern({
            noParams: staticUrls.labLessons,
            withTeamIdAndSubject: staticUrls.labSubjectLessonsSkillsStats,
        }),
        teams: getPattern({
            noParams: staticUrls.labClasses,
            withTeamId: staticUrls.labClassStudents,
        }),
        journal: getPattern({
            noParams: staticUrls.labJournal,
            withTeamIdAndSubject: staticUrls.labSubjectJournal,
        }),
        KPKs: staticUrls.labTeacherTrainingCourses,
        hrReserve: getPattern({
            noParams: staticUrls.labTeacherReserve,
            withTeamIdAndSubject: staticUrls.labClassTeacherReserve,
        }),
        portfolio: staticUrls.labPortfolio,
    };

    return [
        {
            active:
                subject !== 'main' &&
                !isMatchPath.deepLabLibraryMain &&
                (classId
                    ? isMatchPath.labLibrarySubject || isMatchPath.labLibrarySubjectCollection
                    : isMatchPath.deepLabLibrary || isMatchPath.labLibrarySubjectCommonCourseContainer),
            className: 'qa_header-menu-item-library',
            content: i18n('header.link.subjects'),
            disabled: isMenuDisabled,
            href: newLabAccess.materials ? newLabPath.materials : oldLabPath.materials,
            id: 'subjects',
            metricSlug: 'subjects',
            navigationId: TEACHER_COMMON_MENU_ITEMS.LIBRARY,
        },
        {
            active: classId ? isMatchPath.labClassLessons : isMatchPath.labLessons,
            className: 'qa_header-menu-item-lessons',
            content: i18n('header.link.myLessons'),
            disabled: isMenuDisabled,
            href: oldLabPath.lessons,
            id: 'mylessons',
            metricSlug: 'lessons',
            navigationId: TEACHER_COMMON_MENU_ITEMS.MY_LESSONS,
        },
        {
            active: classId
                ? isMatchPath.labClassJournal || isMatchPath.labCourseJournal || isMatchPath.labStudentSubject
                : isMatchPath.labJournal,
            className: 'qa_header-menu-item-journal',
            content: i18n('header.link.journal'),
            disabled: isMenuDisabled,
            href: newLabAccess.journal ? newLabPath.journal : oldLabPath.journal,
            id: 'journal',
            metricSlug: 'journal',
            navigationId: TEACHER_COMMON_MENU_ITEMS.JOURNAL,
        },
        {
            active: classId ? isMatchPath.labClass : isMatchPath.labClasses,
            className: 'qa_header-menu-item-teams',
            content: i18n('header.link.myTeams'),
            disabled: isMenuDisabled,
            href: newLabAccess.teams ? newLabPath.teams : oldLabPath.teams,
            id: 'teams',
            metricSlug: 'classes',
            navigationId: TEACHER_COMMON_MENU_ITEMS.TEAMS,
        },
        {
            className: 'qa_header-teacher',
            content: i18n('header.link.teacher'),
            disabled: isMenuDisabled,
            id: 'teacher',
            items: [
                featureFlags?.TeacherTrainingCoursesEnabled && {
                    active: isMatchPath.labTeacherTrainingCourses,
                    className: 'qa_header-menu-item-teacher-training-courses',
                    content: i18n('header.link.teacherTrainingCourses'),
                    disabled: isMenuDisabled,
                    href: newLabAccess.KPKs ? newLabPath.KPKs : oldLabPath.KPKs,
                    id: 'training-courses',
                    metricSlug: 'teacher-training_courses',
                    navigationId: TEACHER_COMMON_MENU_ITEMS.TEACHER_TRAINING_COURSES,
                },
                newLabAccess?.portfolio && {
                    active: isMatchPath.labPortfolio,
                    className: 'qa_header-menu-item-portfolio',
                    content: i18n('header.link.portfolio'),
                    disabled: isMenuDisabled,
                    href: newLabAccess.portfolio ? newLabPath.portfolio : oldLabPath.portfolio,
                    id: 'portfolio',
                    metricSlug: 'portfolio',
                    navigationId: TEACHER_COMMON_MENU_ITEMS.PORTFOLIO,
                },
                isInformaticsTeacher && {
                    active: false, // только в шредингере
                    className: 'qa_header-menu-item-teacher-reserve',
                    content: i18n('header.link.teacherReserve'),
                    disabled: isMenuDisabled,
                    href: newLabAccess.hrReserve ? newLabPath.hrReserve : oldLabPath.hrReserve,
                    id: 'teacher-reserve',
                    metricSlug: 'teacher-reserve',
                    navigationId: TEACHER_COMMON_MENU_ITEMS.TEACHER_RESERVE,
                },
                newLabAccess?.liveEvents && {
                    active: false, // только в шредингере
                    className: 'qa_header-menu-item-live-events',
                    content: i18n('header.link.liveEvents'),
                    disabled: isMenuDisabled,
                    href: newLabPath.liveEvents,
                    id: 'live-events',
                    navigationId: TEACHER_COMMON_MENU_ITEMS.LIVE_EVENTS,
                },
            ].filter(Boolean),
        },
        {
            active: classId ? isMatchPath.labClassSpecCourseStub : isMatchPath.labSpecCourse,
            className: 'qa_header-menu-item-spec-courses',
            content: i18n('header.link.speccourse'),
            disabled: isMenuDisabled,
            href: getCurrentSpecCourseUrl({ staticUrls, classId, specCourseId }),
            id: 'speccourse',
            metricSlug: 'spec',
            navigationId: TEACHER_COMMON_MENU_ITEMS.OLYMPIAD,
        },
        isContentManager && {
            active: isMatchPath.cmHome,
            content: i18n('header.link.cms'),
            disabled: isMenuDisabled,
            href: cmHome,
            id: 'cms',
        },
        {
            id: 'help',
            className: 'qa_header-menu-item-help',
            content: i18n('header.help'),
            href: i18n('header.link.help.support'),
            metricSlug: 'help',
            notificationId: TEACHER_HELP_MENU_ITEMS.SUPPORT,
            target: '_blank',
        },
    ]
        .filter(Boolean)
        .map((item) =>
            mapLinkParamsToNavItems({
                item: item as LinkParams | SubmenuLinkParams,
                notificationIds,
                viewedNotifications,
                onPress: onLinkPress,
                classId,
                subject,
                specCourseId,
                getRouterPath,
                search,
            })
        );
}
